import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useExtractServerError } from 'hooks';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/index';

import GlobalClaimsUi from './GlobalClaims.ui';
import useGlobalClaimsQuery from './useGlobalClaimsQuery';
import { fromIsoDate, toIsoDate } from '../DirectClaims/format';
import { GlobalClaimsParams } from './GlobalClaims.types';

const DEFAULT_ROWS = 50;

function pageNumber(value: string | null, fallback: number) {
  const number = Number(value);
  return value !== null && Number.isInteger(number) && number >= 0
    ? number
    : fallback;
}

function readParams(searchParams: URLSearchParams): GlobalClaimsParams {
  const rows = pageNumber(searchParams.get('rows'), DEFAULT_ROWS);

  return {
    search: searchParams.get('search') || '',
    stage: searchParams.get('stage') || '',
    category: searchParams.get('category') || '',
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
    first: pageNumber(searchParams.get('first'), 0),
    rows: ROWS_PER_PAGE_OPTIONS.includes(rows) ? rows : DEFAULT_ROWS,
  };
}

// Empty filters stay out of the URL, so a plain link is the full list.
function toSearchParams(params: GlobalClaimsParams) {
  const entries = Object.entries(params)
    .filter(([, value]) => value !== '')
    .map(([key, value]) => [key, String(value)]);

  return Object.fromEntries(entries);
}

export default function GlobalClaims() {
  //* Hooks Init
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * The URL is the query: filters and page, so a shared link opens the same
   * page of claims. With nothing in it the list starts at the newest claim.
   */
  const params = useMemo(() => readParams(searchParams), [searchParams]);

  //* Local State
  const [search, setSearch] = useState(params.search);
  const [dates, setDates] = useState<(Date | null)[]>([
    fromIsoDate(params.startDate),
    fromIsoDate(params.endDate),
  ]);

  const claimsQuery = useGlobalClaimsQuery(params);
  const { extractErrorMessage } = useExtractServerError();

  const claims = claimsQuery.data?.data.data ?? [];
  const totalRecords = claimsQuery.data?.data.total ?? 0;

  //* Handlers
  // Any new filter starts again from the first page.
  function update(next: Partial<GlobalClaimsParams>) {
    setSearchParams(toSearchParams({ ...params, first: 0, ...next }));
  }

  function handleSearchChange(_name: string, value: string) {
    setSearch(value);
  }

  function handleSubmitSearch() {
    update({ search: search.trim() });
  }

  function handleDateChange(event: any) {
    if (!Array.isArray(event.value)) {
      setDates([null, null]);
      update({ startDate: '', endDate: '' });
      return;
    }

    setDates(event.value);

    // The calendar reports a half-picked range after the first click; wait
    // for both ends before asking again.
    const [start, end] = event.value;
    if (start && end) {
      update({ startDate: toIsoDate(start), endDate: toIsoDate(end) });
    }
  }

  function handleStageChange(value: string) {
    update({ stage: value });
  }

  function handleCategoryChange(value: string) {
    update({ category: value });
  }

  function handlePageChange(event: { first: number; rows: number }) {
    setSearchParams(
      toSearchParams({ ...params, first: event.first, rows: event.rows })
    );
  }

  return (
    <GlobalClaimsUi
      claims={claims}
      totalRecords={totalRecords}
      loading={claimsQuery.isFetching}
      errorMessage={
        claimsQuery.isError ? extractErrorMessage(claimsQuery.error) : ''
      }
      search={search}
      params={params}
      dates={dates}
      handlers={{
        handleSearchChange,
        handleSubmitSearch,
        handleDateChange,
        handleStageChange,
        handleCategoryChange,
        handlePageChange,
      }}
    />
  );
}
